import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { RefreshCw } from 'lucide-react';
import { InteriorSidebar } from '../InteriorSidebar';
import type { CameraView } from '../InteriorSidebar';
import { InteriorTopBar } from '../InteriorTopBar';
import { InteriorBottomBar } from '../InteriorBottomBar';
import { OptionsGallery } from '../OptionsGallery';
import { PackageGallery } from '../PackageGallery';
import { SubVariantGallery } from '../SubVariantGallery';
import { NavigationRoomsPanel } from '../NavigationRoomsPanel';
import { InteriorLayoutProps } from './types';
import { useTheme } from '../../styles/ThemeContext';
export function LayoutStandard({
  menuData,
  selectedItem,
  onSelectItem,
  onSelectOption,
  onCloseOption,
  currentCategory,
  showSettings,
  setShowSettings,
  showRoomsMenu,
  setShowRoomsMenu,
  onExit,
  showNavigation,
  setShowNavigation,
  showRoomsPanel,
  setShowRoomsPanel,
  onSelectRoom,
  onStartGuidedTour,
  isGuidedTourActive,
  tourRoomIndex,
  totalTourRooms,
  currentTourRoomName,
  onNextTourRoom,
  onPrevTourRoom,
  onStopTour,
  isStepByStep,
  stepIndex,
  totalSteps,
  currentStepItem,
  currentStepCategory,
  onNextStep,
  onPrevStep,
  onToggleStepMode,
  designMode,
  onDesignModeChange,
  customizationPercent,
  onOpenSettings,
  onOpenControls,
  onOpenNavigate,
  settingsTab,
  isAssistantActive,
  onToggleAssistant,
  hideAssistantButton
}: InteriorLayoutProps) {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeCameraId, setActiveCameraId] = useState('cam-overview');
  const [cameraViewCount, setCameraViewCount] = useState<2 | 3 | 4>(4);
  const {
    isDark
  } = useTheme();
  // Mock camera views — in production these come from the 3D engine per item
  const cameraViews: CameraView[] = [{
    id: 'cam-overview',
    name: 'Kitchen Overview',
    thumbnail: 'https://cdn.magicpatterns.com/uploads/xxSXWRAKLFmBCMDtXjRPxw/Layer_1.png'
  }, {
    id: 'cam-upper',
    name: 'Upper Cabinets',
    thumbnail: 'https://cdn.magicpatterns.com/uploads/uRj6GwmwjHPLdjmXkZsynS/Layer_2.png'
  }, {
    id: 'cam-backsplash',
    name: 'Backsplash View',
    thumbnail: 'https://cdn.magicpatterns.com/uploads/4GVWuKbzrVCWp1Ptz4mSid/Layer_3.png'
  }, {
    id: 'cam-lower',
    name: 'Lower Cabinets',
    thumbnail: 'https://cdn.magicpatterns.com/uploads/5E1xHpJvLaU8LUJ93L8pkf/Layer_4.png'
  }];
  const handleSelectCamera = (id: string) => {
    setActiveCameraId(id);
    console.log('Switch to camera:', id);
  };
  // Auto-collapse sidebar during guided tour or assistant mode
  const effectiveSidebarCollapsed = isGuidedTourActive || isAssistantActive ? true : isSidebarCollapsed;
  // Show switch view when an item is selected or in step-by-step mode
  const showSwitchView = !!selectedItem || isStepByStep;
  return <>
      <InteriorSidebar menuData={menuData} isCollapsed={effectiveSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} onSelectItem={onSelectItem} selectedItemId={selectedItem?.id} designMode={designMode}
    // Step-by-step props
    isStepByStep={isStepByStep} stepIndex={stepIndex} totalSteps={totalSteps} currentStepItem={currentStepItem} currentStepCategory={currentStepCategory} onNextStep={onNextStep} onPrevStep={onPrevStep}
    // Camera views
    cameraViews={isStepByStep ? cameraViews.slice(0, cameraViewCount) : undefined} activeCameraId={activeCameraId} onSelectCamera={handleSelectCamera} cameraViewCount={cameraViewCount} onCameraViewCountChange={setCameraViewCount} data-id="element-1111" />

      <InteriorTopBar onReset={() => console.log('Reset')} onSettings={() => onOpenSettings ? onOpenSettings() : setShowSettings(!showSettings)} onControls={() => onOpenControls ? onOpenControls() : setShowSettings(!showSettings)} onNavigate={onOpenNavigate} onAssistant={hideAssistantButton ? undefined : onToggleAssistant} onExit={onExit} showSettings={showSettings && settingsTab === 'settings'} showControls={showSettings && settingsTab === 'controls'} isAssistantActive={isAssistantActive} data-id="element-1112" />

      {/* Switch View Button — Top Center */}
      <AnimatePresence data-id="element-1113">
        {showSwitchView && !isGuidedTourActive && !isAssistantActive && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.25,
        ease: [0.22, 1, 0.36, 1]
      }} className="fixed top-6 left-1/2 -translate-x-1/2 z-50" data-id="element-1114">
            <button onClick={() => console.log('Switch camera view')} className={`group flex items-center gap-2.5 rounded-xl backdrop-blur-xl border shadow-2xl transition-all duration-200 ${isStepByStep ? isDark ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/30 px-5 py-3' : 'bg-teal-500/15 border-teal-400/40 text-teal-700 hover:bg-teal-500/25 px-5 py-3' : isDark ? 'bg-slate-900/80 border-white/10 text-white/70 hover:text-white hover:bg-slate-900/90 px-4 py-2.5' : 'bg-white/80 border-white/40 text-gray-600 hover:text-gray-900 hover:bg-white/90 px-4 py-2.5'}`} data-id="element-1115">
              {isStepByStep && <span className="relative flex h-2 w-2" data-id="element-1116">
                  <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${isDark ? 'bg-cyan-400' : 'bg-teal-500'}`} data-id="element-1117" />
                  <span className={`relative inline-flex rounded-full h-2 w-2 ${isDark ? 'bg-cyan-400' : 'bg-teal-500'}`} data-id="element-1118" />
                </span>}
              <RefreshCw className={`transition-transform duration-300 group-hover:rotate-180 ${isStepByStep ? 'w-5 h-5' : 'w-4 h-4'}`} data-id="element-1119" />
              <span className={`font-bold uppercase tracking-wider ${isStepByStep ? 'text-sm' : 'text-xs'}`} data-id="element-1120">
                Switch View
              </span>
            </button>
          </motion.div>}
      </AnimatePresence>

      {!isAssistantActive && <InteriorBottomBar modelName="Bellevue" customizationPercent={customizationPercent} isStepByStep={isStepByStep} stepIndex={stepIndex} totalSteps={totalSteps} currentStepItem={currentStepItem} currentStepCategory={currentStepCategory} onNextStep={onNextStep} onPrevStep={onPrevStep} onToggleStepMode={onToggleStepMode} sidebarOpen={!effectiveSidebarCollapsed} leftOffset={!effectiveSidebarCollapsed ? 360 : 100} showNavigation={showNavigation} onToggleNavigation={() => setShowNavigation(!showNavigation)} showRoomsPanel={showRoomsPanel} onToggleRooms={() => setShowRoomsPanel(!showRoomsPanel)} onStartGuidedTour={onStartGuidedTour} isGuidedTourActive={isGuidedTourActive} tourRoomIndex={tourRoomIndex} totalTourRooms={totalTourRooms} currentTourRoomName={currentTourRoomName} onNextTourRoom={onNextTourRoom} onPrevTourRoom={onPrevTourRoom} onStopTour={onStopTour} data-id="element-1121" />}

      {/* Hide Navigation & Rooms panels during guided tour or assistant */}
      {!isGuidedTourActive && !isAssistantActive && <NavigationRoomsPanel showNavigation={showNavigation} showRooms={showRoomsPanel} onToggleNavigation={() => setShowNavigation(!showNavigation)} onToggleRooms={() => setShowRoomsPanel(!showRoomsPanel)} onSelectRoom={onSelectRoom} onStartGuidedTour={onStartGuidedTour} isGuidedTourActive={isGuidedTourActive} hideTabBar={true} data-id="element-1122" />}

      {/* Hide options gallery during guided tour or assistant */}
      {!isGuidedTourActive && !isAssistantActive && <AnimatePresence data-id="element-1123">
          {selectedItem?.isPackage ? <PackageGallery onClose={onCloseOption} docked={false} data-id="element-1124" /> : selectedItem?.children && selectedItem.children.some(child => {
        if (child.options && child.options.length > 0) return true;
        if (child.children) return child.children.some(gc => gc.options && gc.options.length > 0);
        return false;
      }) ? <SubVariantGallery parentItem={selectedItem} onClose={onCloseOption} onSelectOption={onSelectOption} category={currentCategory} data-id="element-1125" /> : selectedItem?.options ? <OptionsGallery options={selectedItem.options} onSelect={onSelectOption} onClose={onCloseOption} category={currentCategory} itemName={selectedItem.name} data-id="element-1126" /> : null}
        </AnimatePresence>}
    </>;
}